import { Button } from "antd";
import type { InvoiceVo } from "@zhihaoscm/entity";
import { S_INVOICE_STATE } from "@zhihaoscm/meta";
import { dateTimeFormat, fmtMoney, fmtNum } from "@zhihaoscm/utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fileService } from "@/api/fileService";
import { tradeInvoiceService } from "@/api/tradeInvoiceService";
import { ProTable, type ProColumn } from "@/components/common";
import dayjs from "dayjs";
import "./invoice.css";
interface InvoiceRow extends InvoiceVo {
    id: InvoiceVo["invoice"]["id"];
    orderId: string;
    amount: number;
    createdTime: string;
    invoiceDate: string;
    state: number;
}
/** 我的发票展示开票统计、筛选列表、详情和票据预览。 */
export function MyInvoicePage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [summary, setSummary] = useState({ totalAmount: 0, totalCount: 0 });
    useEffect(() => {
        void tradeInvoiceService
            .totalCount({})
            .then(data =>
                setSummary({ totalAmount: (data as any).totalAmount ?? 0, totalCount: (data as any).totalCount ?? 0 })
            )
            .catch(() => undefined);
    }, []);
    const stateOptions = S_INVOICE_STATE.options.map(item => ({ label: item.label, value: Number(item.value) }));
    const columns = useMemo<ProColumn<InvoiceRow>[]>(
        () => [
            {
                key: "id",
                title: "开票编号",
                width: 160,
                search: { key: "invoiceId", type: "input", placeholder: "请输入开票编号搜索" },
                render: (_, row) => (
                    <Button type="link" onClick={() => navigate(`/orderCenter/myInvoice/detail/${row.id}`)}>
                        {row.id}
                    </Button>
                )
            },
            {
                key: "orderId",
                title: "订单编号",
                width: 160,
                search: { key: "orderId", type: "input", placeholder: "请输入订单编号搜索" },
                render: (value, row) =>
                    !row.order?.del && row.order?.id
                        ? <Button type="link">{String(value ?? "--")}</Button>
                        : String(value ?? "--")
            },
            {
                key: "amount",
                title: "开票金额(元)",
                minWidth: 130,
                align: "right",
                headerAlign: "right",
                render: value => fmtMoney(value as number | string | undefined)
            },
            {
                key: "createdTime",
                title: "创建时间",
                minWidth: 180,
                search: {
                    key: "createdTime",
                    type: "dateRange",
                    span: 8,
                    rangePlaceholders: ["创建开始日期", "创建结束日期"],
                    rangeSeparator: "至"
                },
                render: value => (value ? dateTimeFormat(String(value)) : "--")
            },
            {
                key: "invoiceDate",
                title: "开票日期",
                minWidth: 180,
                render: value => (value ? dayjs(String(value)).format("YYYY-MM-DD") : "--")
            },
            {
                key: "state",
                title: "状态",
                minWidth: 72,
                enum: stateOptions,
                search: { key: "state", type: "select", placeholder: "请选择状态", options: stateOptions, order: 5 }
            },
            {
                key: "operation",
                title: "操作",
                width: 120,
                fixed: "right",
                render: (_, row) => (
                    <Button
                        type="link"
                        onClick={async () => {
                            const fileId = row.invoice.invalidFileId || row.invoice.fileId;
                            if (!fileId) return;
                            const file = await fileService.getFileById(fileId);
                            window.open(file.path, "_blank");
                        }}
                    >
                        预览
                    </Button>
                )
            }
        ],
        [navigate]
    );
    /** 旧版日期范围转为接口 beginTime/endTime，并从最终参数中移除 createdTime。 */ const request = async (params: Record<string, unknown>) => {
        const { createdTime, ...restParams } = params;
        const range = createdTime as [dayjs.Dayjs, dayjs.Dayjs] | undefined;
        const requestParams = {
            ...restParams,
            beginTime: range?.[0]?.startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            endTime: range?.[1]?.endOf("day").format("YYYY-MM-DD HH:mm:ss")
        };
        const data = await tradeInvoiceService.customAppPaging(requestParams);
        return {
            ...data,
            list: data.list.map(row => ({
                ...row,
                id: row.invoice.id,
                orderId: row.order?.id ?? row.invoice.orderId,
                amount: row.invoice.amount,
                createdTime: row.invoice.createdTime,
                invoiceDate: row.invoice.invoiceDate,
                state: row.invoice.state
            }))
        };
    };
    return (
        <section className="section-card invoice-list-page">
            <div className="invoice-summary">
                <div>
                    <span>已开票总金额(元)</span>
                    <strong>{fmtMoney(summary.totalAmount)}</strong>
                </div>
                <div>
                    <span>已开票(笔)</span>
                    <strong>{fmtNum(summary.totalCount)}</strong>
                </div>
            </div>
            <ProTable
                title="发票列表"
                rowKey="id"
                columns={columns}
                request={request}
                initialParams={{ orderId: searchParams.get("orderId") || undefined }}
            />
        </section>
    );
}
